import { useState, useEffect, useCallback } from 'react';
import {
  Pokemon,
  Move,
  Nature,
  getAllPokemon,
  getAllMoves,
  getAllNatures,
  getAllAbilities,
  getAllItems,
  getSetCategories,
  getPokemonSetsByCategory,
  getSetByName,
  getPokemonByName,
  getPokemonById,
  getMoveByName,
  calculateStat,
  applyStatStage,
  getDefaultPokemon,
  getDefaultMove
} from '../utils/pokemonData';

// Types for calculator state
export interface CalculatorMove {
  name: string;
  power: number;
  type: string;
  category: 'Physical' | 'Special' | 'Status';
  crit: boolean;
  zmove: boolean;
  damage: string;
}

export interface CalculatorPokemon {
  pokemon: Pokemon | null;
  selectedSetName: string; // Track which set was applied
  level: number;
  nature: Nature | null;
  ability: string;
  item: string;
  status: string;
  gender: string;
  types: string[]; // Added back for editable type overrides
  moves: CalculatorMove[];
  ivs: {
    hp: number;
    attack: number;
    defense: number;
    sp_attack: number;
    sp_defense: number;
    speed: number;
  };
  evs: {
    hp: number;
    attack: number;
    defense: number;
    sp_attack: number;
    sp_defense: number;
    speed: number;
  };
  stages: {
    hp: number;
    attack: number;
    defense: number;
    sp_attack: number;
    sp_defense: number;
    speed: number;
  };
  currentHP: {
    current: number;
    total: number;
    percentage: number;
  };
  calculatedStats: {
    hp: number;
    attack: number;
    defense: number;
    sp_attack: number;
    sp_defense: number;
    speed: number;
  };
}

export interface BattleConditions {
  terrain: string[];
  weather: string[];
  globalConditions: string[];
  leftConditions: string[];
  rightConditions: string[];
}

const createDefaultMove = (): CalculatorMove => ({
  name: '(No Move)',
  power: 0,
  type: 'Normal',
  category: 'Physical',
  crit: false,
  zmove: false,
  damage: '-'
});

const createDefaultPokemon = (): CalculatorPokemon => {
  const defaultPokemon = getDefaultPokemon();
  const defaultNature = getAllNatures().find(n => n.name === 'Hardy') || null;

  return {
    pokemon: defaultPokemon,
    selectedSetName: '', // No set initially selected
    level: 100,
    nature: defaultNature,
    ability: 'Snow Warning',
    item: '(None)',
    status: 'Healthy',
    gender: 'Genderless',
    types: [], // Default to empty, will be filled from Pokemon data or user selection
    moves: [
      createDefaultMove(),
      createDefaultMove(),
      createDefaultMove(),
      createDefaultMove()
    ],
    ivs: {
      hp: 31,
      attack: 31,
      defense: 31,
      sp_attack: 31,
      sp_defense: 31,
      speed: 31
    },
    evs: {
      hp: 0,
      attack: 0,
      defense: 0,
      sp_attack: 0,
      sp_defense: 0,
      speed: 0
    },
    stages: {
      hp: 0,
      attack: 0,
      defense: 0,
      sp_attack: 0,
      sp_defense: 0,
      speed: 0
    },
    currentHP: {
      current: 321,
      total: 321,
      percentage: 100
    },
    calculatedStats: {
      hp: 321,
      attack: 220,
      defense: 186,
      sp_attack: 220,
      sp_defense: 206,
      speed: 156
    }
  };
};

export const useCalculator = () => {
  const [leftPokemon, setLeftPokemon] = useState<CalculatorPokemon>(createDefaultPokemon());
  const [rightPokemon, setRightPokemon] = useState<CalculatorPokemon>(createDefaultPokemon());
  const [battleConditions, setBattleConditions] = useState<BattleConditions>({
    terrain: [],
    weather: ['Clear'],
    globalConditions: [],
    leftConditions: [],
    rightConditions: []
  });

  // Data arrays
  const [allPokemon, setAllPokemon] = useState<Pokemon[]>([]);
  const [allMoves, setAllMoves] = useState<Move[]>([]);
  const [allNatures, setAllNatures] = useState<Nature[]>([]);
  const [allAbilities, setAllAbilities] = useState<string[]>([]);
  const [allItems, setAllItems] = useState<string[]>([]);
  const [allSetCategories, setAllSetCategories] = useState<string[]>([]);

  // Set selection state - independent for each side
  const [leftSetCategory, setLeftSetCategory] = useState<string>('All');
  const [rightSetCategory, setRightSetCategory] = useState<string>('All');
  const [leftAvailablePokemonSets, setLeftAvailablePokemonSets] = useState<string[]>([]);
  const [rightAvailablePokemonSets, setRightAvailablePokemonSets] = useState<string[]>([]);

  // Load data on mount
  useEffect(() => {
    const pokemon = getAllPokemon();
    const moves = getAllMoves();
    const natures = getAllNatures();
    const abilities = getAllAbilities();
    const items = getAllItems();
    const setCategories = getSetCategories();

    console.log('Data loaded:', {
      pokemon: pokemon.length,
      moves: moves.length,
      natures: natures.length,
      abilities: abilities.length,
      items: items.length,
      setCategories: setCategories.length
    });

    setAllPokemon(pokemon);
    setAllMoves(moves);
    setAllNatures(natures);
    setAllAbilities(abilities);
    setAllItems(items);
    setAllSetCategories(setCategories);
  }, []);

  // Update available Pokemon sets when category changes for left side
  useEffect(() => {
    if (leftSetCategory) {
      const pokemonSets = getPokemonSetsByCategory(leftSetCategory);
      setLeftAvailablePokemonSets(pokemonSets);
    }
  }, [leftSetCategory]);

  // Update available Pokemon sets when category changes for right side
  useEffect(() => {
    if (rightSetCategory) {
      const pokemonSets = getPokemonSetsByCategory(rightSetCategory);
      setRightAvailablePokemonSets(pokemonSets);
    }
  }, [rightSetCategory]);

  // Calculate stats function
  const calculatePokemonStats = useCallback((pokemon: CalculatorPokemon): CalculatorPokemon => {
    if (!pokemon.pokemon) return pokemon;

    const statNames: (keyof typeof pokemon.ivs)[] = ['hp', 'attack', 'defense', 'sp_attack', 'sp_defense', 'speed'];
    const calculatedStats = { ...pokemon.calculatedStats };

    statNames.forEach(statName => {
      const baseStat = pokemon.pokemon![`base_${statName}` as keyof Pokemon] as number;
      const stat = calculateStat(
        baseStat,
        pokemon.ivs[statName],
        pokemon.evs[statName],
        pokemon.level,
        pokemon.nature,
        statName,
        statName === 'hp'
      );

      // Apply stat stages (except HP)
      calculatedStats[statName] = statName === 'hp'
        ? stat
        : applyStatStage(stat, pokemon.stages[statName]);
    });

    // Update current HP
    const currentHP = {
      current: Math.min(pokemon.currentHP.current, calculatedStats.hp),
      total: calculatedStats.hp,
      percentage: Math.round((Math.min(pokemon.currentHP.current, calculatedStats.hp) / calculatedStats.hp) * 100)
    };

    return {
      ...pokemon,
      calculatedStats,
      currentHP
    };
  }, []);

  // Update Pokemon
  const updateLeftPokemon = useCallback((updates: Partial<CalculatorPokemon>) => {
    setLeftPokemon(prev => {
      const updated = { ...prev, ...updates };
      return calculatePokemonStats(updated);
    });
  }, [calculatePokemonStats]);

  const updateRightPokemon = useCallback((updates: Partial<CalculatorPokemon>) => {
    setRightPokemon(prev => {
      const updated = { ...prev, ...updates };
      return calculatePokemonStats(updated);
    });
  }, [calculatePokemonStats]);

  // Pokemon selection
  const selectLeftPokemon = useCallback((pokemonName: string) => {
    const pokemon = getPokemonByName(pokemonName);
    if (pokemon) {
      updateLeftPokemon({
        pokemon,
        currentHP: {
          current: calculateStat(pokemon.base_hp, 31, 0, 100, null, 'hp', true),
          total: calculateStat(pokemon.base_hp, 31, 0, 100, null, 'hp', true),
          percentage: 100
        }
      });
    }
  }, [updateLeftPokemon]);

  const selectRightPokemon = useCallback((pokemonName: string) => {
    const pokemon = getPokemonByName(pokemonName);
    if (pokemon) {
      updateRightPokemon({
        pokemon,
        currentHP: {
          current: calculateStat(pokemon.base_hp, 31, 0, 100, null, 'hp', true),
          total: calculateStat(pokemon.base_hp, 31, 0, 100, null, 'hp', true),
          percentage: 100
        }
      });
    }
  }, [updateRightPokemon]);

  // Move selection
  const updateLeftMove = useCallback((index: number, moveName: string) => {
    const move = getMoveByName(moveName) || getDefaultMove();
    const newMoves = [...leftPokemon.moves];
    newMoves[index] = {
      name: move.name,
      power: move.power,
      type: move.type,
      category: move.category,
      crit: false,
      zmove: false,
      damage: '-' // Will be calculated later
    };
    updateLeftPokemon({ moves: newMoves });
  }, [leftPokemon.moves, updateLeftPokemon]);

  const updateRightMove = useCallback((index: number, moveName: string) => {
    const move = getMoveByName(moveName) || getDefaultMove();
    const newMoves = [...rightPokemon.moves];
    newMoves[index] = {
      name: move.name,
      power: move.power,
      type: move.type,
      category: move.category,
      crit: false,
      zmove: false,
      damage: '-' // Will be calculated later
    };
    updateRightPokemon({ moves: newMoves });
  }, [rightPokemon.moves, updateRightPokemon]);

  // Battle conditions
  const toggleWeather = useCallback((weather: string) => {
    setBattleConditions(prev => ({
      ...prev,
      weather: prev.weather.includes(weather)
        ? prev.weather.filter(w => w !== weather)
        : [weather] // Only one weather at a time
    }));
  }, []);

  const toggleTerrain = useCallback((terrain: string) => {
    setBattleConditions(prev => ({
      ...prev,
      terrain: prev.terrain.includes(terrain)
        ? prev.terrain.filter(t => t !== terrain)
        : [terrain] // Only one terrain at a time
    }));
  }, []);

  const toggleGlobalCondition = useCallback((condition: string) => {
    setBattleConditions(prev => ({
      ...prev,
      globalConditions: prev.globalConditions.includes(condition)
        ? prev.globalConditions.filter(c => c !== condition)
        : [...prev.globalConditions, condition]
    }));
  }, []);

  const toggleLeftCondition = useCallback((condition: string) => {
    if (condition === 'Spikes') {
      setBattleConditions(prev => {
        const current = prev.leftConditions.find(c => c.startsWith('Spikes'));
        let next: string | null = null;
        if (!current) next = 'Spikes 1';
        else if (current === 'Spikes 1') next = 'Spikes 2';
        else if (current === 'Spikes 2') next = 'Spikes 3';
        const filtered = prev.leftConditions.filter(c => !c.startsWith('Spikes'));
        return { ...prev, leftConditions: next ? [...filtered, next] : filtered };
      });
    } else {
      setBattleConditions(prev => ({
        ...prev,
        leftConditions: prev.leftConditions.includes(condition)
          ? prev.leftConditions.filter(c => c !== condition)
          : [...prev.leftConditions, condition]
      }));
    }
  }, []);

  const toggleRightCondition = useCallback((condition: string) => {
    if (condition === 'Spikes') {
      setBattleConditions(prev => {
        const current = prev.rightConditions.find(c => c.startsWith('Spikes'));
        let next: string | null = null;
        if (!current) next = 'Spikes 1';
        else if (current === 'Spikes 1') next = 'Spikes 2';
        else if (current === 'Spikes 2') next = 'Spikes 3';
        const filtered = prev.rightConditions.filter(c => !c.startsWith('Spikes'));
        return { ...prev, rightConditions: next ? [...filtered, next] : filtered };
      });
    } else {
      setBattleConditions(prev => ({
        ...prev,
        rightConditions: prev.rightConditions.includes(condition)
          ? prev.rightConditions.filter(c => c !== condition)
          : [...prev.rightConditions, condition]
      }));
    }
  }, []);

  // Recalculate stats when relevant data changes
  // Set application function
  const applySetToPokemon = useCallback((setName: string, side: 'left' | 'right') => {
    // Skip header separators
    if (setName.startsWith('---')) return;

    const setData = getSetByName(setName);
    if (!setData) return;

    const pokemon = getPokemonById(setData.pokedexid);
    const nature = allNatures.find(n => n.name === setData.nature);

    // Calculate HP for currentHP initialization
    const maxHP = pokemon ? calculateStat(
      pokemon.base_hp,
      setData.hp_iv,
      setData.hp_ev,
      setData.level,
      nature || null,
      'hp',
      true
    ) : 100;

    const appliedSet: Partial<CalculatorPokemon> = {
      pokemon,
      selectedSetName: setName,
      level: setData.level,
      nature,
      ability: setData.ability,
      item: setData.item,
      ivs: {
        hp: setData.hp_iv,
        attack: setData.atk_iv,
        defense: setData.def_iv,
        sp_attack: setData.sp_atk_iv,
        sp_defense: setData.sp_def_iv,
        speed: setData.spd_iv
      },
      evs: {
        hp: setData.hp_ev,
        attack: setData.atk_ev,
        defense: setData.def_ev,
        sp_attack: setData.sp_atk_ev,
        sp_defense: setData.sp_def_ev,
        speed: setData.spd_ev
      },
      currentHP: {
        current: maxHP,
        total: maxHP,
        percentage: 100
      }
    };

    // Apply moves if available
    if (setData.moves && setData.moves.length > 0) {
      const moves = setData.moves.map((moveName: string) => {
        const move = getMoveByName(moveName);
        return {
          name: moveName,
          power: move?.power || 0,
          type: move?.type || 'Normal',
          category: move?.category || 'Physical',
          crit: false,
          zmove: false,
          damage: ''
        };
      });

      // Pad with empty moves if needed
      while (moves.length < 4) {
        moves.push({
          name: '',
          power: 0,
          type: 'Normal',
          category: 'Physical',
          crit: false,
          zmove: false,
          damage: ''
        });
      }

      appliedSet.moves = moves;
    }

    if (side === 'left') {
      updateLeftPokemon(appliedSet);
    } else {
      updateRightPokemon(appliedSet);
    }
  }, [allNatures, updateLeftPokemon, updateRightPokemon]);

  // Set category selection functions for each side
  const selectLeftSetCategory = useCallback((category: string) => {
    setLeftSetCategory(category);
  }, []);

  const selectRightSetCategory = useCallback((category: string) => {
    setRightSetCategory(category);
  }, []);

  useEffect(() => {
    setLeftPokemon(prev => calculatePokemonStats(prev));
    setRightPokemon(prev => calculatePokemonStats(prev));
  }, []); // Only run once on mount

  // Function to refresh set categories (for when custom sets are added/removed)
  const refreshSetCategories = useCallback(() => {
    const setCategories = getSetCategories();
    setAllSetCategories(setCategories);

    // Refresh available sets for current categories
    if (leftSetCategory) {
      const pokemonSets = getPokemonSetsByCategory(leftSetCategory);
      setLeftAvailablePokemonSets(pokemonSets);
    }
    if (rightSetCategory) {
      const pokemonSets = getPokemonSetsByCategory(rightSetCategory);
      setRightAvailablePokemonSets(pokemonSets);
    }
  }, [leftSetCategory, rightSetCategory]);

  return {
    // State
    leftPokemon,
    rightPokemon,
    battleConditions,

    // Data
    allPokemon,
    allMoves,
    allNatures,
    allAbilities,
    allItems,
    allSetCategories,
    leftSetCategory,
    rightSetCategory,
    leftAvailablePokemonSets,
    rightAvailablePokemonSets,

    // Actions
    updateLeftPokemon,
    updateRightPokemon,
    selectLeftPokemon,
    selectRightPokemon,
    updateLeftMove,
    updateRightMove,
    applySetToPokemon,
    selectLeftSetCategory,
    selectRightSetCategory,
    toggleWeather,
    toggleTerrain,
    toggleGlobalCondition,
    toggleLeftCondition,
    toggleRightCondition,
    refreshSetCategories
  };
};
